import { Link, useLocation } from "react-router-dom";
import React, { useState, useRef, useEffect } from "react";
import { useSetting } from "../../../contexts/settings";
import { BOOK_CALL_URL } from "../../../config/cta";
import { createPortal } from "react-dom";
import { MENU } from "../../../config/MENU";
import type { IMenuCategory, IMenuItem } from "../../../config/MENU";
import { CTA, trackAttrs } from "../../../utils/track";
import { isExternalHref } from "../../../utils/internalHref";
import {
  TbBrandGithubFilled,
  TbBrandInstagram,
  TbBrandLinkedin,
  TbBrandTwitter,
  TbMail,
  TbBrandYoutube,
  TbRss,
} from "react-icons/tb";

const socialLinks = [
  {
    href: "https://github.com/ehsangazar",
    icon: TbBrandGithubFilled,
    label: "Github",
  },
  {
    href: "https://www.linkedin.com/in/gazar",
    icon: TbBrandLinkedin,
    label: "Linkedin",
  },
  {
    href: "https://twitter.com/ehsangazar",
    icon: TbBrandTwitter,
    label: "Twitter",
  },
  {
    href: "https://www.instagram.com/gazar.day",
    icon: TbBrandInstagram,
    label: "Instagram",
  },
  {
    href: "https://www.youtube.com/@gazarbreakpoint",
    icon: TbBrandYoutube,
    label: "YouTube",
  },
  {
    href: "mailto:me@gazar.dev",
    icon: TbMail,
    label: "Email",
  },
  {
    href: "/feed.xml",
    icon: TbRss,
    label: "RSS Feed",
  },
];

const Navigation = () => {
  const { pathname } = useLocation();
  // Booking is on-site now, so the default is a route. The setting still wins
  // (admin.gazar.dev → Settings), which is the escape hatch if the flow ever has
  // to point somewhere external again — hence `isInternalBooking`: an internal
  // path must render as a <Link> so it doesn't hard-navigate, and an external one
  // must keep target="_blank".
  const bookingUrl = useSetting("bookingUrl", BOOK_CALL_URL);
  const isInternalBooking = bookingUrl.startsWith("/");
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const dropdownCloseTimeout = useRef<NodeJS.Timeout | null>(null);
  const submenuCloseTimeout = useRef<NodeJS.Timeout | null>(null);

  const isActiveMenu = React.useCallback(
    (item: IMenuItem | IMenuCategory | undefined) => {
      if (!item) return false;
      if (item.path && item.path === "/" && pathname === "/") {
        return true;
      }
      if (item.path && item.path !== "/" && pathname.includes(item.path)) {
        return true;
      }
      if ("items" in item && Array.isArray(item.items)) {
        return item.items.some((subItem) => subItem.path === pathname);
      }
      return false;
    },
    [pathname]
  );

  // Lock/unlock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (!isMobileMenuOpen) return;
    function handleClick(e: MouseEvent) {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target as Node)) {
        setIsMobileMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isMobileMenuOpen]);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [pathname]);

  const NavItem = ({ item, isDropdown = false, className = "", target = "_self", hasSubmenus = false }: { item: IMenuItem | IMenuCategory | undefined; isDropdown?: boolean, className?: string, target?: string, hasSubmenus?: boolean }) => {
  if (!item) return null;
  const isActive = isActiveMenu(item);
  const baseClass =
    "relative px-3 py-2 min-h-[48px] rounded-smooth transition-colors duration-150 text-sm font-heading font-semibold uppercase flex items-center gap-1.5 border border-transparent";
  const activeClass = isActive
    ? "text-white bg-brand-navy font-bold border-brand-navy"
    : "text-brand-navy hover:text-white hover:bg-brand-navy hover:border-brand-navy";
  const buttonClassName = `${baseClass} ${activeClass} ${className}`;
    
    if (isDropdown && "items" in item && Array.isArray(item.items)) {
      return (
        <div
          className="relative"
          onMouseEnter={() => {
            if (dropdownCloseTimeout.current) {
              clearTimeout(dropdownCloseTimeout.current);
              dropdownCloseTimeout.current = null;
            }
            setOpenDropdown(item.title || null);
          }}
          onMouseLeave={() => {
            dropdownCloseTimeout.current = setTimeout(() => {
              setOpenDropdown(null);
            }, 150);
          }}
        >
          <button
            className={buttonClassName}
            onClick={() => setOpenDropdown(openDropdown === item.title ? null : item.title)}
          >
            {item.title}
            <svg
              className={`w-2.5 h-2.5 transition-transform duration-200 ${openDropdown === item.title ? "rotate-180" : ""}`}
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {openDropdown === item.title && (
            <div
              className="absolute left-0 mt-1 w-48 bg-brand-cream border border-border-strong rounded-smooth z-30"
              style={{ overflow: 'visible' }}
              onMouseEnter={() => {
                if (dropdownCloseTimeout.current) {
                  clearTimeout(dropdownCloseTimeout.current);
                  dropdownCloseTimeout.current = null;
                }
                setOpenDropdown(item.title || null);
              }}
              onMouseLeave={() => {
                dropdownCloseTimeout.current = setTimeout(() => {
                  setOpenDropdown(null);
                  setOpenSubmenu(null);
                }, 200);
              }}
            >
              {(item.items || []).map((subItem: any, index: number) => {
                const totalItems = item.items?.length || 0;
                const isLastItem = index === totalItems - 1;
                
                // Check if this item has a submenu
                if (subItem.hasSubmenu && subItem.submenu && subItem.submenu.length > 0) {
                  return (
                    <div
                      key={subItem.name}
                      className="relative group/submenu"
                      onMouseEnter={() => {
                        if (submenuCloseTimeout.current) {
                          clearTimeout(submenuCloseTimeout.current);
                          submenuCloseTimeout.current = null;
                        }
                        setOpenSubmenu(subItem.name);
                      }}
                      onMouseLeave={() => {
                        submenuCloseTimeout.current = setTimeout(() => {
                          setOpenSubmenu(null);
                        }, 250);
                      }}
                    >
                      <div
                      className={`flex items-center justify-between px-4 py-2 text-sm font-heading font-bold uppercase transition-colors duration-150 ${
                          isLastItem ? '' : 'border-b border-border-subtle'
                        } cursor-pointer text-brand-navy hover:bg-brand-navy hover:text-white ${
                          openSubmenu === subItem.name ? 'bg-brand-navy text-white' : ''
                        }`}
                      >
                        <span>{subItem.title}</span>
                        <svg
                          className={`w-3 h-3 transition-transform duration-200 ${
                            openSubmenu === subItem.name ? 'translate-x-0.5' : ''
                          }`}
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                      {openSubmenu === subItem.name && (
                        <>
                          {/* Invisible bridge to prevent gap hover issues */}
                          <div className="absolute left-full top-0 w-4 h-full z-40"></div>
                          
                          <div
                          className="absolute left-[calc(100%+1px)] top-0 w-48 bg-brand-cream border border-border-strong rounded-smooth z-50"
                            onMouseEnter={() => {
                              if (submenuCloseTimeout.current) {
                                clearTimeout(submenuCloseTimeout.current);
                                submenuCloseTimeout.current = null;
                              }
                              setOpenSubmenu(subItem.name);
                            }}
                            onMouseLeave={() => {
                              submenuCloseTimeout.current = setTimeout(() => {
                                setOpenSubmenu(null);
                              }, 200);
                            }}
                          >
                            {/* Submenu Header */}
                            <div className="bg-brand-cream px-4 py-2 border-b border-border-subtle">
                              <span className="text-sm font-heading font-black text-brand-navy uppercase">
                                {subItem.title}
                              </span>
                            </div>
                            
                            {/* Submenu Items */}
                            {subItem.submenu.map((nestedItem: any, nestedIndex: number) => (
                              <a
                                key={nestedItem.name}
                                href={nestedItem.path}
                                className={`block px-4 py-2 text-sm font-heading uppercase transition-all duration-150 ${
                                  nestedIndex === subItem.submenu.length - 1 ? '' : 'border-b border-border-subtle'
                                } ${
                                  isActiveMenu(nestedItem)
                                    ? "text-white bg-brand-navy font-bold"
                                    : "text-brand-navy hover:bg-brand-navy hover:text-white font-semibold"
                                }`}
                                onClick={() => {
                                  setIsMobileMenuOpen(false);
                                  setOpenDropdown(null);
                                  setOpenSubmenu(null);
                                }}
                              >
                                <div className="flex items-center gap-2">
                                  <span className="w-1.5 h-1.5 rounded-full bg-current opacity-50"></span>
                                  {nestedItem.title}
                                </div>
                              </a>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  );
                }
                
                // Regular menu item without submenu
                return (
                  <a
                    key={subItem.name}
                    href={subItem.path}
                    className={`block px-4 py-2 text-sm font-heading font-bold uppercase transition-all duration-150 ${
                      isLastItem ? '' : 'border-b border-border-subtle'
                    } ${
                      isActiveMenu(subItem)
                        ? "text-white bg-brand-navy"
                        : "text-brand-navy hover:bg-brand-navy hover:text-white"
                    }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <span>{subItem.title}</span>
                  </a>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    return (
      <a
        href={item.path}
        target={target}
        rel={item.isExternal ? "noreferrer" : undefined}
        className={buttonClassName}
        onClick={() => setIsMobileMenuOpen(false)}
      >
        {item.title}
      </a>
    );
  };

  // Top-nav mega-menu. Lead with the on-pillar "Advanced" group (system design,
  // AI, leadership) to keep the nav staff/architect-focused. Beginner/off-pillar
  // topics flagged `hidden` in MENU are filtered out here; they stay routable and
  // listed on the /learnings "All Topics" page for SEO.
  const visibleLearning = (MENU.LEARNING.items || [])
    .filter((item) => typeof item.path === "string")
    .filter((item) => !item.hidden);
  const learningDropdown = {
    title: "Learning",
    items: [
      { title: "All Topics", path: "/learnings", name: "learnings" },
      // The hub decks. Added 12 Aug 2026 into this dropdown rather than as a
      // ninth top-level item, which the desktop bar has no room for.
      { title: "Slide Decks", path: "/decks", name: "decks" },
      {
        title: "Advanced",
        path: "/learnings#advanced",
        name: "advanced",
        hasSubmenu: true,
        submenu: visibleLearning.filter((item) => [
          "System Design",
          "Artificial Intelligence",
          "Algorithm",
          "Data Structure",
          "Leadership"
        ].includes(item.title || ""))
      },
      {
        title: "Backend",
        path: "/learnings#backend",
        name: "backend",
        hasSubmenu: true,
        submenu: visibleLearning.filter((item) => [
          "SQL",
          "Database",
          "Security",
          "DevOps",
          "Design Pattern"
        ].includes(item.title || ""))
      },
      {
        title: "Frontend",
        path: "/learnings#frontend",
        name: "frontend",
        hasSubmenu: true,
        submenu: visibleLearning.filter((item) => [
          "HTML and CSS",
          "TypeScript",
          "Clean Code",
          "Testing",
          "Accessibility"
        ].includes(item.title || ""))
      },
    ],
  };

  return (
    <div className="sticky top-0 z-50 bg-brand-cream border-b border-border-subtle">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <nav className="flex items-center w-full py-1.5 md:py-2">
          {/* Mobile: Social Media - Far Left */}
          <div className="flex nav:hidden items-center gap-1">
            {socialLinks.slice(0, 4).map((link) => {
              const IconComponent = link.icon;
              const isExternal = isExternalHref(link.href);
              return (
                <a
                  key={link.label}
                  href={link.href}
                  {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  className="inline-flex items-center justify-center w-12 h-12 rounded-smooth border border-border-subtle bg-brand-cream text-brand-navy transition-colors duration-150 hover:bg-brand-navy hover:text-white hover:border-brand-navy"
                >
                  <IconComponent size={12} />
                </a>
              );
            })}
          </div>

          {/* Desktop Menu - Home, content (Reviews→Podcast→Learnings), services (Mentorship), identity (About) */}
          <div className="hidden nav:flex items-center gap-0.5 flex-1">
            <NavItem item={MENU.HOME} />
            <NavItem item={MENU.REVIEWS} />
            <NavItem item={MENU.VIDEOS} />
            <NavItem item={learningDropdown} isDropdown />
            {MENU.COURSES && <NavItem item={MENU.COURSES} />}
            <NavItem item={MENU.MENTORSHIP} />
            <NavItem item={MENU.ABOUT} isDropdown={Array.isArray(MENU.ABOUT.items)} />
            <NavItem item={MENU.SEARCH} />
          </div>

          {/* Spacer below the nav breakpoint to push hamburger to far right */}
          <div className="nav:hidden flex-1"></div>

          {/* Auth Buttons - Desktop Only */}
          <div className="hidden nav:flex items-center gap-2">
            {/* Language switch. `lang` and `hrefLang` are both set so the label
                renders in a Persian face and a crawler is told where the link
                goes, and it is a plain <a> rather than a <Link> because
                fa.gazar.dev is a different app on a different origin. */}
            <a
              href="https://fa.gazar.dev/"
              lang="fa"
              hrefLang="fa"
              className="font-heading text-brand-navy font-bold px-4 py-2.5 rounded-smooth border border-border-strong hover:bg-brand-navy hover:text-white hover:border-brand-navy transition-colors duration-150 text-base"
            >
              فارسی
            </a>
            {isInternalBooking ? (
              <Link
                to={bookingUrl}
                {...trackAttrs(CTA.bookCall, 'nav_desktop')}
                className="font-heading bg-brand-navy text-white font-bold px-5 py-2.5 rounded-smooth border border-brand-navy hover:opacity-90 transition-opacity duration-150 text-base"
              >
                Let's Talk
              </Link>
            ) : (
              <a
                href={bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                {...trackAttrs(CTA.bookCall, 'nav_desktop')}
                className="font-heading bg-brand-navy text-white font-bold px-5 py-2.5 rounded-smooth border border-brand-navy hover:opacity-90 transition-opacity duration-150 text-base"
              >
                Let's Talk
              </a>
            )}
          </div>

          {/* Mobile Menu Button - Far Right */}
          <button
            className="nav:hidden p-1.5 text-brand-navy hover:text-white focus:outline-none focus:ring-1 focus:ring-brand-navy/40 rounded-smooth bg-brand-cream border border-border-strong transition-colors duration-150 hover:bg-brand-navy"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              {isMobileMenuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </nav>
      </div>

      {/* Mobile Menu Portal */}
      {isMobileMenuOpen && createPortal(
        <div className="fixed inset-0 z-50 nav:hidden">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-brand-navy bg-opacity-50" 
            onClick={() => setIsMobileMenuOpen(false)}
          />
          
          {/* Menu Panel */}
          <div
            ref={mobileMenuRef}
            className="absolute right-0 top-0 h-full w-64 max-w-[78vw] bg-brand-cream border border-border-strong"
          >
            {/* Header */}
            <div className="flex justify-between items-center p-4 border-b border-border-strong bg-brand-cream">
              <h2 className="font-heading text-base font-bold text-brand-navy uppercase">Menu</h2>
              <button
                className="p-1.5 text-brand-navy hover:text-white focus:outline-none focus:ring-1 focus:ring-brand-navy/40 rounded-smooth border border-border-strong transition-colors duration-150 hover:bg-brand-navy"
                onClick={() => setIsMobileMenuOpen(false)}
                aria-label="Close menu"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            
            {/* Menu Content */}
            <div className="h-[calc(100vh-64px)] overflow-y-auto bg-brand-cream">
              <div className="p-4 space-y-5">
                {/* Main Navigation */}
                <div className="space-y-1">
                  <NavItem item={MENU.HOME} />
                  <NavItem item={MENU.SEARCH} />
                  <NavItem item={MENU.REVIEWS} />
                  <a href="/learnings" className="block px-4 py-2 text-sm font-heading font-bold uppercase transition-all duration-150 text-brand-navy border border-transparent hover:bg-brand-navy hover:text-white rounded-smooth" onClick={() => setIsMobileMenuOpen(false)}>Learnings</a>
                  <NavItem item={MENU.VIDEOS} />
                  {MENU.COURSES && <NavItem item={MENU.COURSES} />}
                  <NavItem item={MENU.MENTORSHIP} />
                  <a
                    href="https://fa.gazar.dev/"
                    lang="fa"
                    hrefLang="fa"
                    className="block px-4 py-2 text-sm font-heading font-bold transition-all duration-150 text-brand-navy border border-transparent hover:bg-brand-navy hover:text-white rounded-smooth"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    فارسی
                  </a>
                </div>

                {/* Learnings */}
                <div className="space-y-1.5">
                  <div className="font-heading text-brand-navy font-semibold px-3 py-2 cursor-default select-none text-sm uppercase">
                    Learning
                  </div>
                  <div className="space-y-0.5">
                    {learningDropdown.items.map((subItem: any) =>
                      subItem.hasSubmenu && subItem.submenu?.length ? (
                        <div key={subItem.name} className="space-y-0.5">
                          <div className="px-3 py-1 text-xs font-heading font-bold uppercase text-brand-navy/70">
                            {subItem.title}
                          </div>
                          {subItem.submenu.map((nestedItem: any) => (
                            <a
                              key={nestedItem.name}
                              href={nestedItem.path}
                              className="block px-3 py-1.5 pl-5 rounded-smooth text-sm font-heading uppercase transition-colors duration-150 text-brand-navy border border-transparent hover:bg-brand-navy hover:text-white hover:border-brand-navy"
                              onClick={() => setIsMobileMenuOpen(false)}
                            >
                              {nestedItem.title}
                            </a>
                          ))}
                        </div>
                      ) : (
                        <a
                          key={subItem.name}
                          href={subItem.path}
                          className="block px-3 py-1.5 rounded-smooth text-sm font-heading uppercase transition-colors duration-150 text-brand-navy border border-transparent hover:bg-brand-navy hover:text-white font-bold hover:border-brand-navy"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          {subItem.title}
                        </a>
                      )
                    )}
                  </div>
                </div>

                {/* About */}
                <div className="space-y-1.5">
                  <div className="font-heading text-brand-navy font-semibold px-3 py-2 cursor-default select-none text-sm uppercase">
                    About
                  </div>
                  <div className="space-y-0.5">
                    {(MENU.ABOUT.items || []).map((subItem) => (
                      <a 
                        key={subItem.name} 
                        href={subItem.path} 
                        className="block px-3 py-1.5 rounded-smooth text-sm font-heading uppercase transition-colors duration-150 text-brand-navy border border-transparent hover:bg-brand-navy hover:text-white hover:border-brand-navy" 
                        onClick={() => setIsMobileMenuOpen(false)} 
                      >
                        {subItem.title}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Footer */}
            <div className="absolute bottom-0 left-0 right-0 p-4 pt-10 border-t border-border-strong bg-brand-cream">
              {isInternalBooking ? (
                <Link
                  to={bookingUrl}
                  {...trackAttrs(CTA.bookCall, 'nav_mobile')}
                  className="block w-full font-heading bg-brand-navy text-white font-bold px-3.5 py-2.5 rounded-smooth border border-brand-navy hover:opacity-90 transition-opacity duration-150 text-base text-center"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Let's Talk
                </Link>
              ) : (
                <a
                  href={bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  {...trackAttrs(CTA.bookCall, 'nav_mobile')}
                  className="block w-full font-heading bg-brand-navy text-white font-bold px-3.5 py-2.5 rounded-smooth border border-brand-navy hover:opacity-90 transition-opacity duration-150 text-base text-center"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Let's Talk
                </a>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Navigation;
